// src/pages/Pricing.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './Pricing.css';
import { 
  FiCheck, 
  FiX, 
  FiZap, 
  FiStar, 
  FiAward,
  FiCalendar,
  FiMapPin,
  FiUsers,
  FiShoppingBag,
  FiMessageSquare,
  FiBell,
  FiCreditCard,
  FiHeadphones,
  FiTrendingUp,
  FiBarChart2,
  FiSettings,
  FiGlobe
} from 'react-icons/fi';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const pricingPlans = [
    {
      name: 'Standard',
      description: 'Core app — no dashboard subscription',
      icon: <FiZap />,
      color: '#6366f1',
      price: {
        monthly: 0,
        annual: 0
      },
      adminFee: '10% per completed booking',
      support: 'Uncontracted operational support',
      bookingLimit: 'Unlimited bookings/month',
      trial: null,
      features: [
        { text: 'Community app for businesses', included: true },
        { text: 'Location Services', included: true },
        { text: 'User Profiles with linked Business Profiles', included: true },
        { text: 'List available Services with Pricing', included: true },
        { text: 'Appointment Scheduling', included: true },
        { text: 'Booking and confirmation system', included: true },
        { text: 'Messaging Service', included: true },
        { text: 'Appointment tracking and reminders', included: true },
        { text: 'Payment processing', included: true },
        { text: 'Uncontracted Operational Support', included: true },
        { text: 'Web-based administrative console', included: false },
        { text: 'Transaction reports and statements', included: false },
        { text: 'Inclusive advertisements', included: false },
        { text: 'Custom branding', included: false }
      ],
      cta: 'Get Started Free',
      popular: false
    },
    {
      name: 'Pro',
      description: 'For growing businesses',
      icon: <FiStar />,
      color: '#f59e0b',
      price: {
        monthly: 299,
        annual: 2868 // 5% discount applied
      },
      adminFee: '10% per completed booking',
      support: '20 hours monthly',
      bookingLimit: 'Unlimited bookings/month',
      trial: '14 days',
      features: [
        { text: 'All core SnapConX app features', included: true },
        { text: 'Two inclusive advertisements per month', included: true },
        { text: 'Web-based administrative console', included: true },
        { text: 'Transaction reports and statements', included: true },
        { text: 'Priority email support', included: true },
        { text: 'Advanced analytics', included: true },
        { text: 'Custom branding', included: false },
        { text: 'Unlimited advertisements', included: false },
        { text: 'Multiple locations', included: false }
      ],
      cta: 'Try Dashboard Free for 14 Days',
      popular: true
    },
    {
      name: 'Premium',
      description: 'For established businesses',
      icon: <FiTrendingUp />,
      color: '#ec4899',
      price: {
        monthly: 599,
        annual: 5740 // 5% discount applied
      },
      adminFee: '10% per completed booking',
      support: '40 hours monthly',
      bookingLimit: 'Unlimited bookings/month',
      trial: '14 days',
      features: [
        { text: 'All Pro Dashboard features', included: true },
        { text: 'Eight inclusive advertisements per month', included: true },
        { text: 'Priority phone & email support', included: true },
        { text: 'Advanced reporting & analytics', included: true },
        { text: 'Client & admin app', included: true },
        { text: 'Brand customization', included: true },
        { text: 'API access', included: true },
        { text: 'Multiple locations (up to 3)', included: false },
        { text: 'White-label solution', included: false }
      ],
      cta: 'Try Dashboard Free for 14 Days',
      popular: false
    },
    {
      name: 'Branded',
      description: 'Complete custom solution',
      icon: <FiAward />,
      color: '#10b981',
      price: {
        monthly: 999,
        annual: 9540 // 5% discount applied
      },
      additionalPricing: [
        { locations: '1-3', price: 999 },
        { locations: '4-10', price: 1999 },
        { locations: '11-20', price: 3499 },
        { locations: '21+', price: 'R 333 per location' }
      ],
      adminFee: '10% per completed booking',
      support: 'Unlimited',
      bookingLimit: 'Unlimited bookings/month',
      trial: '14 days',
      features: [
        { text: 'Unlimited functionality', included: true },
        { text: 'Custom-branded app for your business', included: true },
        { text: 'App exclusive to your business and clients', included: true },
        { text: 'Unlimited advertisements', included: true },
        { text: 'Unlimited support', included: true },
        { text: 'Complete functionality with no restrictions', included: true },
        { text: 'White-label solution', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Custom integrations', included: true }
      ],
      cta: 'Try Dashboard Free for 14 Days',
      popular: false,
      enterprise: true
    }
  ];

  const handleCTAClick = (plan) => {
    if (currentUser) {
      // Navigate to dashboard or subscription page
      navigate('/dashboard');
    } else {
      // Navigate to login/signup
      navigate('/login', { state: { selectedPlan: plan.name } });
    }
  };

  const calculateSavings = (monthly, annual) => {
    if (monthly === 0) return 0;
    const yearlyMonthly = monthly * 12;
    return yearlyMonthly - annual;
  };

  return (
    <div className="pricing-page">
      <div className="pricing-hero">
        <h1 className="pricing-title">Dashboard Packages</h1>
        <p className="pricing-subtitle">
          Optional paid web-dashboard tools for businesses that need more. Explore analytics, transaction reports and additional business management features.
        </p>
        
        <section className="dashboard-package-showcase" aria-labelledby="dashboard-showcase-title">
          <div className="dashboard-showcase-copy">
            <p className="dashboard-showcase-eyebrow">Optional paid Business Dashboard</p>
            <h2 id="dashboard-showcase-title">More insight. More control.</h2>
            <p>Bring your business into focus with web-based management tools, analytics and transaction reports. Choose the Dashboard Package that gives you the additional functionality you need.</p>
            <p>The core SnapConX app is free to join and can be used without a monthly dashboard subscription.</p>
            <a className="dashboard-showcase-link" href="#dashboard-packages">Compare Dashboard Packages</a>
            <ul aria-label="Dashboard benefits">
              <li>Business insights</li>
              <li>Transaction reports</li>
              <li>Optional subscription</li>
            </ul>
          </div>
          <img src="/images/carousel-dashboard.svg" width="1200" height="800" alt="Illustrative SnapConX Business Dashboard with booking totals, transaction reports and an activity chart. Sample data." />
        </section>
        <div className="dashboard-package-intro">
          <h2>Use the app. Add a dashboard when you need it.</h2>
          <p>SnapConX is free to join. List your business and receive bookings through the core app without a monthly dashboard subscription.</p>
          <p>Pro, Premium and Branded are optional paid Dashboard Packages. The free app is shown below for comparison. Platform fees still apply: 10% per completed booking and 5% of the product total.</p>
        </div>
        <h2 id="dashboard-packages" className="dashboard-package-heading">Choose your optional Dashboard Package</h2>
        <div className="billing-toggle">
          <button
            className={billingCycle === 'monthly' ? 'active' : ''}
            aria-pressed={billingCycle === 'monthly'}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button
            className={billingCycle === 'annual' ? 'active' : ''}
            aria-pressed={billingCycle === 'annual'}
            onClick={() => setBillingCycle('annual')}
          >
            Annual
            <span className="savings-badge">Save 5%</span>
          </button>
        </div>
      </div>

      <div className="pricing-grid">
        {pricingPlans.map((plan, index) => (
          <div
            key={index}
            className={`pricing-card ${plan.popular ? 'popular' : ''} ${plan.enterprise ? 'enterprise' : ''}`}
            style={{ '--plan-color': plan.color }}
          >
            {plan.popular && (
              <div className="popular-badge">
                <FiStar /> Most Popular
              </div>
            )}

            <div className="plan-header">
              <div className="plan-icon" style={{ color: plan.color }}>
                {plan.icon}
              </div>
              <h3 className="plan-name">{plan.price.monthly === 0 ? 'SnapConX App' : `${plan.name} Dashboard`}</h3>
              <p className="plan-description">{plan.description}</p>
              {plan.trial && (
                <div className="trial-badge">
                  <FiZap /> {plan.trial} free trial
                </div>
              )}
            </div>

            <div className="plan-pricing">
              <div className="price-display">
                <span className="currency">R</span>
                <span className="amount">
                  {billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual}
                </span>
                <span className="period">
                  /{billingCycle === 'monthly' ? 'month' : 'year'}
                </span>
              </div>
              {billingCycle === 'annual' && plan.price.monthly > 0 && (
                <p className="savings-text">
                  Save R{calculateSavings(plan.price.monthly, plan.price.annual)} per year
                </p>
              )}
              <div className="admin-fee">
                <small><strong>Platform Fees</strong></small>
                <br />
                <small>Bookings: {plan.adminFee}</small>
                <br />
                <small>Products: 5% of the product total</small>
              </div>
            </div>

            {plan.additionalPricing && (
              <div className="additional-pricing">
                <h4>Location-based Pricing:</h4>
                <ul>
                  {plan.additionalPricing.map((tier, i) => (
                    <li key={i}>
                      {tier.locations} locations: {typeof tier.price === 'number' ? `R${tier.price}` : tier.price}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="plan-specs">
              <div className="spec-item">
                <FiCalendar />
                <span>{plan.bookingLimit}</span>
              </div>
              <div className="spec-item">
                <FiHeadphones />
                <span>{plan.support}</span>
              </div>
            </div>

            <button
              className="plan-cta"
              onClick={() => handleCTAClick(plan)}
            >
              {plan.cta}
            </button>

            <div className="plan-features">
              <h4>What's included:</h4>
              <ul>
                {plan.features.map((feature, i) => (
                  <li key={i} className={feature.included ? 'included' : 'not-included'}>
                    <span className="feature-icon" role="img" aria-label={feature.included ? 'Included' : 'Not included'}>
                      {feature.included ? <FiCheck /> : <FiX />}
                    </span>
                    <span className="feature-text">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="pricing-comparison">
        <h2>Compare the App and Dashboard Packages</h2>
        <p className="comparison-hint">On smaller screens, scroll across to compare the app and three optional Dashboard Packages.</p>
        <div className="comparison-table-wrapper" role="region" aria-label="App and Dashboard Package comparison" tabIndex={0}>
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Features</th>
                <th>SnapConX App</th>
                <th>Pro Dashboard</th>
                <th>Premium Dashboard</th>
                <th>Branded Dashboard</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="feature-name">
                  <FiCalendar /> Appointment Scheduling
                </td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
              </tr>
              <tr>
                <td className="feature-name">
                  <FiMapPin /> Location Services
                </td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td className="feature-name">
                  <FiCreditCard /> Payment Processing
                </td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
              </tr>
              <tr>
                <td className="feature-name">
                  <FiBarChart2 /> Analytics & Reports
                </td>
                <td>Basic</td>
                <td><FiCheck className="check" /></td>
                <td>Advanced</td>
                <td>Advanced</td>
              </tr>
              <tr>
                <td className="feature-name">
                  <FiSettings /> Admin Console
                </td>
                <td><FiX className="cross" /></td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
                <td><FiCheck className="check" /></td>
              </tr>
              <tr>
                <td className="feature-name">
                  <FiTrendingUp /> Advertisements
                </td>
                <td><FiX className="cross" /></td>
                <td>2/month</td>
                <td>8/month</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td className="feature-name">
                  <FiGlobe /> Custom Branding
                </td>
                <td><FiX className="cross" /></td>
                <td><FiX className="cross" /></td>
                <td><FiCheck className="check" /></td>
                <td>Full White-label</td>
              </tr>
              <tr>
                <td className="feature-name">
                  <FiHeadphones /> Support
                </td>
                <td>Uncontracted</td>
                <td>20hrs/month</td>
                <td>40hrs/month</td>
                <td>Unlimited</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>Do I need a Dashboard Package to use SnapConX?</h3>
            <p>
              No. You can use the core SnapConX app without a monthly dashboard subscription. Paid Dashboard Packages are optional extras for web-based business management, analytics and reporting. Platform fees still apply.
            </p>
          </div>
          <div className="faq-item">
            <h3>Can I change Dashboard Packages later?</h3>
            <p>
              Yes! You can upgrade or downgrade your Dashboard Package at any time. Changes take effect
              immediately and billing is prorated.
            </p>
          </div>
          <div className="faq-item">
            <h3>What payment methods do you accept?</h3>
            <p>
              We accept all major credit cards and debit cards through our secure payment processor.
              Annual Dashboard Packages can be paid via bank transfer.
            </p>
          </div>
          <div className="faq-item">
            <h3>Is there a setup fee?</h3>
            <p>
              No setup fees for the core app or Pro and Premium Dashboard Packages. Branded Dashboard Packages may include a
              one-time setup fee depending on customization requirements.
            </p>
          </div>
        </div>
      </div>

      <div className="pricing-cta-section">
        <h2>Need more tools for your business?</h2>
        <p>Try an optional paid Business Dashboard with a 14-day free trial. No dashboard subscription is required to use the core app.</p>
        <button className="cta-button" onClick={() => navigate(currentUser ? '/dashboard' : '/login')}>
          {currentUser ? 'My Dashboard' : 'Start Dashboard Trial'}
        </button>
      </div>
    </div>
  );
};

export default Pricing;
